/**
 * Process Engine Execution API Client
 * Handles execution of processes and workflows
 */

import { BaseApiClient, ApiResponse, RequestOptions } from './baseApiClient';
import {
  ExecuteProcessRequest,
  ExecuteProcessResponse,
  GetExecutionStatusRequestData,
  ExecutionControlRequestData,
} from '../types/processEngineExecution.types';
import type {
  ExecutionStatusResponse,
  ExecutionControlResponse,
  ExecutionIdentifier,
} from '@flow-studio/types/execution.types';
import { API_ENDPOINTS } from '../config/apiEndpoints';

class ProcessEngineExecutionApiClient extends BaseApiClient {
  /**
   * Execute a process
   */
  public async executeProcess(
    request: ExecuteProcessRequest,
    options?: RequestOptions
  ): Promise<ApiResponse<ExecuteProcessResponse>> {
    return this.post<ExecuteProcessResponse>(
      API_ENDPOINTS.PROCESS_ENGINE_EXECUTION.EXECUTE,
      request,
      options
    );
  }

  /**
   * Get execution status with progress and node details
   * BaseApiClient.post() wraps this in { metadata, data }, so just provide ExecutionID
   */
  public async getExecutionStatus(
    executionId: ExecutionIdentifier,
    options?: RequestOptions
  ): Promise<ApiResponse<ExecutionStatusResponse>> {
    const request: GetExecutionStatusRequestData = {
      ExecutionID: executionId,
    };
    return this.post<ExecutionStatusResponse>(
      API_ENDPOINTS.PROCESS_ENGINE_EXECUTION.STATUS,
      request,
      options
    );
  }

  /**
   * Pause running execution
   * BaseApiClient.post() wraps this in { metadata, data }, so just provide ExecutionID
   */
  public async pauseExecution(
    executionId: ExecutionIdentifier,
    options?: RequestOptions
  ): Promise<ApiResponse<ExecutionControlResponse>> {
    const request: ExecutionControlRequestData = {
      ExecutionID: executionId,
    };
    return this.post<ExecutionControlResponse>(
      API_ENDPOINTS.PROCESS_ENGINE_EXECUTION.PAUSE,
      request,
      options
    );
  }

  /**
   * Resume paused execution
   * BaseApiClient.post() wraps this in { metadata, data }, so just provide ExecutionID
   */
  public async resumeExecution(
    executionId: ExecutionIdentifier,
    options?: RequestOptions
  ): Promise<ApiResponse<ExecutionControlResponse>> {
    const request: ExecutionControlRequestData = {
      ExecutionID: executionId,
    };
    return this.post<ExecutionControlResponse>(
      API_ENDPOINTS.PROCESS_ENGINE_EXECUTION.RESUME,
      request,
      options
    );
  }

  /**
   * Cancel execution
   * BaseApiClient.post() wraps this in { metadata, data }, so just provide ExecutionID
   */
  public async cancelExecution(
    executionId: ExecutionIdentifier,
    options?: RequestOptions
  ): Promise<ApiResponse<ExecutionControlResponse>> {
    const request: ExecutionControlRequestData = {
      ExecutionID: executionId,
    };
    return this.post<ExecutionControlResponse>(
      API_ENDPOINTS.PROCESS_ENGINE_EXECUTION.CANCEL,
      request,
      options
    );
  }

  /**
   * Fetch all node activity logs for a given execution ResID.
   * Used for catch-up after SignalR group join to populate nodeInspectorData
   * for nodes whose ReceiveNodeActivity events fired before the client joined.
   */
  public async getNodeActivityLogsByExecutionResId(
    executionResID: string,
    options?: RequestOptions
  ): Promise<ApiResponse<any>> {
    return this.postQuery<any>(
      API_ENDPOINTS.NODE_ACTIVITY_LOGS.BY_EXECUTION_RES_ID,
      { executionResID, pageSize: 500 },
      options
    );
  }
}

export const processEngineExecutionApiClient =
  new ProcessEngineExecutionApiClient();
